// The screen: reads the read model, posts commands, redraws from what comes back.
//
// It holds NO state of its own beyond what is currently being asked for — the search text, the
// chosen errand, the ordering. There is no local copy of an entry anywhere below this line, and
// that is the point: a screen that kept its own shadow of the store would be a second write model,
// and the two would disagree the first time something failed to be written.
//
// EVERY CHANGE GOES THROUGH A COMMAND, and the redraw is driven by the event that comes back rather
// than by the click. So a change made in another tab — the write model announces it there, and the
// storage event brings it here — lands on this screen the same way as one made in it.
//
// The words come from the markup. <spring:message> resolved them in the reader's language and the
// page handed them over as data attributes; a script carrying its own English would be a second
// translation nobody updates.

import { all, forget, restore, importDocument, exportDocument, clear, subscribe } from './write-model.js';
import { project } from './read-model.js';
import { usable, KEY } from './store.js';
import { download } from './table.js';

const screen = document.getElementById('mydata');

/* Everything below runs only on the screen that asked for it. The bundle is one file loaded by every
   page on the site, so a component that does not check where it is runs two hundred times a day for
   nothing. */
if (screen) {
	start(screen);
}

function start(root) {
	const entries = document.getElementById('mydata-entries');
	const toolbar = document.getElementById('mydata-toolbar');
	const transfer = document.getElementById('mydata-transfer');
	const outcome = document.getElementById('mydata-outcome');
	const counter = document.getElementById('mydata-count');
	const search = document.getElementById('mydata-search');
	const errand = document.getElementById('mydata-errand');
	const sort = document.getElementById('mydata-sort');
	const labels = root.dataset;

	/* What is being ASKED for, which is all the state this screen owns. Not what is stored — that
	   lives in one place, and it is not here. */
	const query = { search: '', errand: '', sort: 'recent' };

	/* Whether this screen has ever had anything on it. Once true it stays true — see `render`. */
	let everFilled = false;

	/** A label with its `{0}`, `{1}` … filled. The braces survived <spring:message> because it was
	    called with no arguments, so MessageFormat never ran — this is the substitution it did not do. */
	function say(label, ...values) {
		return values.reduce((text, value, index) => text.replace('{' + index + '}', value),
			label || '');
	}

	function announce(text, failed) {
		outcome.textContent = text;
		outcome.hidden = !text;
		outcome.classList.toggle('is-failed', !!failed);
	}

	/* The date, in the reader's own language, from the one place that knows it: the document. Written
	   out rather than left as an ISO string, which reads as a machine's answer to a person's question.
	   A value that will not parse is shown as it is rather than as "Invalid Date". */
	function readable(iso) {
		const when = new Date(iso);
		if (Number.isNaN(when.getTime())) return iso;
		return when.toLocaleDateString(document.documentElement.lang || undefined,
			{ year: 'numeric', month: 'long', day: 'numeric' });
	}

	/* One entry, built with DOM calls and textContent throughout. These are someone's own words
	   coming back to them; assembling this with innerHTML would make a screen that scripts whoever
	   reads it, from a store anyone who has used this machine can write to. */
	function draw(entry) {
		const card = document.createElement('article');
		card.className = 'mydata-entry';
		card.dataset.id = entry.id;

		const heading = document.createElement('h2');
		heading.className = 'mydata-entry-title';
		heading.textContent = entry.title || entry.errand;
		card.append(heading);

		const when = document.createElement('p');
		when.className = 'mydata-entry-when';
		when.textContent = say(labels.labelOpened, readable(entry.keptAt));
		card.append(when);

		const list = document.createElement('dl');
		list.className = 'mydata-entry-answers';
		entry.answers.forEach((answer) => {
			const term = document.createElement('dt');
			term.textContent = answer.label;
			const detail = document.createElement('dd');
			detail.textContent = answer.value;
			list.append(term, detail);
		});
		card.append(list);

		const actions = document.createElement('div');
		actions.className = 'mydata-entry-actions';

		// Reopening is what makes a kept answer worth keeping. The address is rebuilt from the origin
		// it was typed on plus the path it was served at — the token that shapes the form is in that
		// path, so an entry reopens the form that asked, not merely a form of the same errand.
		if (entry.source) {
			const reopen = document.createElement('a');
			reopen.className = 'ui-button mydata-entry-reopen';
			reopen.href = (entry.origin || '') + entry.source;
			reopen.textContent = labels.labelReopen;
			actions.append(reopen);
		}

		const remove = document.createElement('button');
		remove.className = 'ui-button tone-danger mydata-entry-delete';
		remove.type = 'button';
		remove.dataset.forget = entry.id;
		remove.textContent = labels.labelDelete;
		actions.append(remove);

		card.append(actions);
		return card;
	}

	/* The errand filter, rebuilt from the facets. The chosen value is put back afterwards, and drops
	   to "everything" when the errand it named no longer has any entries — which happens the moment
	   someone deletes the last one while it is filtered, and would otherwise leave the screen empty
	   with no visible reason. */
	function drawFacets(view) {
		const chosen = query.errand;
		errand.textContent = '';
		const every = document.createElement('option');
		every.value = '';
		every.textContent = labels.labelAll;
		errand.append(every);
		let stillThere = false;
		view.facets.forEach((facet) => {
			const option = document.createElement('option');
			option.value = facet.errand;
			option.textContent = facet.label + ' (' + facet.count + ')';
			errand.append(option);
			if (facet.errand === chosen) stillThere = true;
		});
		query.errand = stillThere ? chosen : '';
		errand.value = query.errand;
	}

	function render(view) {
		entries.textContent = '';

		if (view.emptiness) {
			const empty = document.createElement('p');
			empty.className = 'mydata-empty';
			// Two different sentences for two different situations: an empty store is not a search
			// that found nothing, and telling someone "no match" when they have never kept anything
			// sends them looking for a filter to clear.
			empty.textContent = view.emptiness === 'store' ? labels.labelEmpty : labels.labelNoMatch;
			entries.append(empty);
		} else {
			const list = document.createDocumentFragment();
			view.rows.forEach((entry) => list.append(draw(entry)));
			entries.append(list);
		}

		/* The toolbar and the transfer block appear only once there is something to filter or move: a
		   search box over an empty store is furniture that promises results.

		   THE TOOLBAR NEVER GOES AWAY AGAIN, and that is not a detail. It sits ABOVE the entries, so
		   withdrawing it pulls the whole page up by its height — and the one moment it would do that
		   is the moment the last entry is deleted, which is exactly when an « undo » has just been
		   offered below. Measured: the page shortened under the pointer and the click meant for
		   « undo » landed on the navbar. The count then reads "0 entries", which is a true thing to
		   say and a better one than a page that jumps. A screen that never had anything still opens
		   bare — the furniture argument only ever applied to that case.

		   The transfer block sits BELOW the outcome, so hiding it moves nothing above it: it keeps
		   the original rule. */
		if (view.total > 0) everFilled = true;
		toolbar.hidden = !everFilled;
		transfer.hidden = view.total === 0;
		counter.textContent = view.matched === 1
			? labels.labelCountOne : say(labels.labelCountMany, view.matched);
		if (view.total > 0) drawFacets(view);
	}

	/** Read, project, draw. The one path by which anything reaches the screen. */
	function refresh() {
		return all().then((kept) => render(project(kept, query)));
	}

	// ── What the screen does ──────────────────────────────────────────────────────────────────────

	search.addEventListener('input', () => {
		query.search = search.value;
		refresh();
	});
	errand.addEventListener('change', () => {
		query.errand = errand.value;
		refresh();
	});
	sort.addEventListener('change', () => {
		query.sort = sort.value;
		refresh();
	});

	/* Delegated rather than bound per card: the cards are replaced on every redraw, and a listener
	   attached to each one is a listener to reattach every time — or to leak. On the ROOT rather than
	   on the list: the composed screens (screens.js) forget through the same attribute, and so get
	   the same undo. */
	root.addEventListener('click', (event) => {
		const button = event.target.closest('[data-forget]');
		if (!button) return;
		forget(button.dataset.forget).then((done) => {
			if (!done.ok) return;
			announce(labels.labelDeleted);
			// The undo is offered from what the command HANDED BACK, not from a copy this screen kept
			// beforehand. Nothing here holds an entry that is no longer in the store.
			const undo = document.createElement('button');
			undo.className = 'ui-button mydata-undo';
			undo.type = 'button';
			undo.textContent = labels.labelUndo;
			undo.addEventListener('click', () => restore(done.entry).then(() => announce('')));
			outcome.append(' ', undo);
		});
	});

	document.getElementById('mydata-export').addEventListener('click', () => {
		exportDocument().then((doc) => {
			download((labels.labelFilename || 'my-data') + '.json', JSON.stringify(doc, null, '\t'),
				'application/json');
		});
	});

	document.getElementById('mydata-import').addEventListener('change', (event) => {
		const file = event.target.files && event.target.files[0];
		if (!file) return;
		file.text()
			.then((text) => importDocument(JSON.parse(text)))
			.then((done) => {
				if (!done.ok) {
					announce(labels.labelUnreadable, true);
					return;
				}
				announce(done.added === 0 ? labels.labelImportedNothing
					: say(labels.labelImported, done.added, done.known));
			})
			// A file that is not JSON at all throws before the command ever runs, and it deserves the
			// same sentence as one that parses into something that is not ours: from where the reader
			// stands, both are "that file is not one of ours".
			.catch(() => announce(labels.labelUnreadable, true));
		// Cleared so that choosing the SAME file again fires `change` a second time. Without this,
		// re-importing after a failure looks like the button has stopped working.
		event.target.value = '';
	});

	/* THE ONLY COMMAND WITH NO UNDO, so it is the one that asks — and it asks in the page.
	   `window.confirm` would be a modal, and a modal is what this house does not use for a deletion;
	   a page of its own would be the preferred shape and there is no route here to give it one. So
	   the button asks in place: the first press turns it into the question, the second answers it,
	   and anything else — a click elsewhere, a moment's pause — puts it back. Nothing is dismissed
	   by accident because nothing was ever covering anything. */
	const clearButton = document.getElementById('mydata-clear');
	const clearLabel = clearButton.querySelector('span').textContent;
	let arming = null;

	function disarm() {
		if (!arming) return;
		clearTimeout(arming);
		arming = null;
		clearButton.classList.remove('is-arming');
		clearButton.querySelector('span').textContent = clearLabel;
	}

	clearButton.addEventListener('click', () => {
		if (!arming) {
			clearButton.classList.add('is-arming');
			clearButton.querySelector('span').textContent = labels.labelClearConfirm;
			// Long enough to read the question, short enough that an armed button is never found
			// armed by whoever comes back to the tab later.
			arming = setTimeout(disarm, 8000);
			return;
		}
		disarm();
		clear().then((done) => announce(done.ok ? labels.labelCleared : '', !done.ok));
	});

	// Anywhere else disarms. Capturing, so it runs before a click that lands on another control and
	// leaves the question standing behind it.
	document.addEventListener('click', (event) => {
		if (!clearButton.contains(event.target)) disarm();
	}, true);

	// ── What happens to the screen ────────────────────────────────────────────────────────────────

	// Redrawn from the EVENT, not from the click that caused it. Same path for a change made here and
	// for one made by a form in another tab.
	subscribe(() => refresh());

	/* The other tab. `storage` fires on every OTHER document sharing this origin, which is exactly
	   the case this covers: a form kept in one tab while this screen is open in another. It does not
	   fire in the tab that wrote, which is why the subscription above exists as well. */
	window.addEventListener('storage', (event) => {
		if (event.key === KEY) refresh();
	});

	/* Nothing works if the browser refuses to store, so that is said once, plainly, instead of
	   letting every action fail on its own terms. */
	usable().then((can) => {
		if (!can) {
			announce(labels.labelUnavailable, true);
			toolbar.hidden = true;
			transfer.hidden = true;
			return;
		}
		refresh();
	});
}
